import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import dayjs from '../../../../utils/dayjsConfig'
import { getCurrentDate } from '../../../../helpers/formatDate'
import {
  buildRouteExecutionPdf,
  buildRouteExportKpis,
  routeBusinessLines
} from './routeExecutionPdfBuilder'
import { ACTIVITIES_DIR } from '../userReportTabs'
import { buildUnifiedVisitDetails, buildUnifiedVisitLookupKey } from '../buildUnifiedVisitDetails'

const COLORS = {
  primary: [162, 158, 157],
  gray: [130, 130, 130],
  border: [200, 200, 200],
  dark: [17, 24, 39],
  accent: [70, 114, 163],
  cardBg: [250, 251, 252]
}

const STATUS_LABELS = {
  visited: 'VISITADO',
  no_visited: 'NO VISITADO',
  pending: 'PENDIENTE',
  no_location: 'SIN GEOCERCA'
}

const formatDateLabel = (value) => {
  if (!value) return '-'

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-'
}

const formatDateTimeLabel = (value) => {
  if (!value) return '-'

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('DD/MM/YYYY - hh:mm A') : '-'
}

const getTextValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  return String(value)
}

const getActivitiesLabel = (activities) => {
  if (!Array.isArray(activities) || !activities.length) {
    return '-'
  }

  return activities.map((activityCode) => ACTIVITIES_DIR[activityCode] || activityCode).join(', ')
}

const getStatusLabel = (status) => {
  if (!status) return '-'

  return STATUS_LABELS[status] || String(status).replace(/_/g, ' ').toUpperCase()
}

const drawSectionTitle = (doc, title, y) => {
  doc.setFontSize(11)
  doc.setTextColor(...COLORS.dark)
  doc.text(String(title), 14, y)
}

const buildRouteVisitMainRow = (row, summary = {}) => {
  const clientLabel = [row?.client_uid, row?.client_name].filter(Boolean).join(' - ') || '-'
  const effectivenessLabel = row?.execution_date != null ? (row?.effective ? 'SI' : 'NO') : '-'
  const totalVisitDuration =
    summary?.visitDuration && summary.visitDuration !== '-' ? summary.visitDuration : null

  return [
    clientLabel,
    row?.in_route ? formatDateLabel(row?.visit_date) : '-',
    row?.execution_date ? formatDateLabel(row?.execution_date) : '-',
    getTextValue(totalVisitDuration || row?.summary?.visit_duration || row?.visit_duration),
    row?.in_route ? 'SI' : 'NO',
    effectivenessLabel,
    getActivitiesLabel(row?.activities),
    getStatusLabel(row?.status)
  ]
}

const buildRouteVisitTableRows = ({
  routeExecutionDownloadData = [],
  activityDownloadData = [],
  timestampDownloadData = [],
  formatActivityName
}) => {
  const { visitSummaryByKey } = buildUnifiedVisitDetails({
    activityDownloadData,
    timestampDownloadData,
    formatActivityName,
    includeTimestampRows: false
  })

  return (routeExecutionDownloadData || []).map((row) => {
    const summary = visitSummaryByKey[buildUnifiedVisitLookupKey(row)] || {}

    return buildRouteVisitMainRow(row, summary)
  })
}

const getDetailSortTime = (detail) => {
  const parsed = detail?.datetime ? dayjs(detail.datetime).valueOf() : Number.NaN
  return Number.isFinite(parsed) ? parsed : 0
}

const getDetailTypeLabel = (type) => {
  const labels = {
    check_in: 'Check-In',
    check_out: 'Check-Out',
    activity: 'Actividad'
  }

  return labels[type] || 'Detalle'
}

const buildVisitDetailCsvHeaderRow = () => [
  'FECHA Y HORA',
  'TIPO',
  'CÓDIGO DE ACTIVIDAD',
  'ACTIVIDAD',
  'MOTIVO/DESCRIPCIÓN',
  'MONTO',
  'DENTRO DE GEOCERCA'
]

const buildVisitDetailCsvRow = (detail) => [
  formatDateTimeLabel(detail?.datetime),
  getDetailTypeLabel(detail?.type),
  getTextValue(detail?.activityCode),
  getTextValue(detail?.label),
  getTextValue(detail?.description),
  getTextValue(detail?.amount),
  getTextValue(detail?.inGeofence || 'N/A')
]

const buildRouteVisitCsvRows = ({
  routeExecutionDownloadData = [],
  activityDownloadData = [],
  timestampDownloadData = [],
  formatActivityName
}) => {
  const { detailsByVisitKey, visitSummaryByKey } = buildUnifiedVisitDetails({
    activityDownloadData,
    timestampDownloadData,
    formatActivityName,
    includeTimestampRows: true
  })

  return (routeExecutionDownloadData || []).flatMap((row) => {
    const lookupKey = buildUnifiedVisitLookupKey(row)
    const summary = lookupKey ? visitSummaryByKey[lookupKey] || {} : {}
    const visitDetails = lookupKey ? detailsByVisitKey[lookupKey] || [] : []
    const mainRow = buildRouteVisitMainRow(row, summary)
    const detailRows = [...visitDetails]
      .sort((left, right) => getDetailSortTime(left) - getDetailSortTime(right))
      .map(buildVisitDetailCsvRow)
  // Repetir las columnas de visita para identificar cada detalle al filtrar en Excel.
    return detailRows.length
      ? detailRows.map((detailRow) => [...mainRow, ...detailRow])
      : [[...mainRow, ...buildVisitDetailCsvHeaderRow().map(() => '-')]]
  })
}

export const buildRouteUserReportCsvRows = ({
  routeCartData,
  geofenceSegment = 'general',
  business,
  salesmanCode,
  routeExecutionDownloadData,
  activityDownloadData,
  timestampDownloadData,
  formatActivityName,
  salesmanName,
  supervisorName,
  startDate,
  endDate
}) => {
  const summaryCards = buildRouteExportKpis(routeCartData, geofenceSegment)
  const tableRows = buildRouteVisitCsvRows({
    routeExecutionDownloadData,
    activityDownloadData,
    timestampDownloadData,
    formatActivityName
  })

  return [
    ['REPORTE:', 'Reporte ejecución de plan de visitas de usuario'],
    ...routeBusinessLines(business).map((line) => [line]),
    ['USUARIO:', [salesmanCode, salesmanName].filter(Boolean).join(' - ') || '-'],
    ['SUPERVISOR:', supervisorName || '-'],
    ['PERIODO:', `${startDate} - ${endDate}`],
    ['GENERADO:', new Date().toLocaleString()],
    [],
    ['VISITAS'],
    ['KPI', 'VALOR'],
    ...summaryCards.flatMap((card) => [
      [card.title, card.value],
      [card.subtitle],
      ...card.metrics,
      ...(card.footer ? [[card.footer]] : [])
    ]),
    [],
    ['TABLA'],
    [
      'CLIENTE',
      'FECHA PLANIFICADA',
      'FECHA EJECUCION',
      'DURACION',
      'DENTRO DE RUTA',
      'EFECTIVIDAD',
      'ACTIVIDADES REALIZADAS',
      'ESTADO',
      ...buildVisitDetailCsvHeaderRow()
    ],
    ...(tableRows.length ? tableRows : [['No hay resultados', ...Array(14).fill('')]])
  ]
}

export { downloadCsvRows } from '../../../../helpers/downloadCsvRows'

export const exportRouteUserReportPDF = (options) => {
  const {
    routeCartData,
    geofenceSegment,
    business,
    salesmanName,
    salesmanCode,
    business_name,
    startDate,
    endDate
  } = options
  const doc = buildRouteExecutionPdf({
    business,
    salesmanName,
    salesmanCode,
    period: `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`,
    generatedAt: dayjs().format('DD/MM/YYYY hh:mm A'),
    kpis: buildRouteExportKpis(routeCartData, geofenceSegment),
    rows: buildRouteVisitTableRows(options)
  })
  const businessLabel = business_name || business?.name || 'negocio'
  const filename = `RouteResumen_${businessLabel}_${getCurrentDate()}.pdf`
  doc.save(filename)
  return filename
}

export const exportTabularUserReportPDF = ({
  reportTitle,
  filename,
  salesmanName,
  startDate,
  endDate,
  summaryLines = [],
  headers = [],
  body = []
}) => {
  const doc = new jsPDF('landscape', 'mm', 'a4')
  const pageWidth = doc.internal.pageSize.getWidth()
  let currentY = 15
  const leftLabelX = 14
  const leftValueX = 40
  const rightLabelX = pageWidth / 2 + 22
  const rightValueX = rightLabelX + 28

  doc.setFontSize(10)
  doc.setTextColor(...COLORS.dark)
  doc.text('REPORTE:', leftLabelX, currentY)
  doc.text(getTextValue(reportTitle), leftValueX, currentY)
  doc.text('PERIODO:', rightLabelX, currentY)
  doc.text(`${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`, rightValueX, currentY)

  currentY += 6
  doc.text('USUARIO:', leftLabelX, currentY)
  doc.text(getTextValue(salesmanName), leftValueX, currentY)
  doc.text('GENERADO:', rightLabelX, currentY)
  doc.text(new Date().toLocaleString(), rightValueX, currentY)

  summaryLines.forEach((line) => {
    currentY += 6
    doc.text(`${line.label}:`, leftLabelX, currentY)
    doc.text(getTextValue(line.value), leftValueX, currentY)
  })

  currentY += 8
  drawSectionTitle(doc, 'Detalle', currentY)

  const emptyRow = [
    (headers.length ? headers : ['']).map((_, index) => (index === 0 ? 'No hay resultados' : ''))
  ]

  autoTable(doc, {
    startY: currentY + 5,
    margin: { left: 14, right: 8 },
    head: [headers],
    body: body.length ? body : emptyRow,
    theme: 'grid',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7,
      minCellHeight: 8
    },
    styles: {
      fontSize: 7,
      cellPadding: 2,
      textColor: COLORS.dark,
      lineColor: COLORS.border,
      lineWidth: 0.2,
      overflow: 'linebreak',
      valign: 'middle'
    },
    alternateRowStyles: {
      fillColor: COLORS.cardBg
    }
  })

  doc.save(filename)
}
